import { ERowTypes } from "api/enums";
import { IPDFCellInput } from "api/interfaces/IPDFCellInput";
import { IPDFCellStyles } from "api/interfaces/IPDFCellStyles";
import ContractModel from "api/models/ContractModel";
import MeasurementModel from "api/models/MeasurementModel";
import MeasurementRowModel from "api/models/MeasurementRowModel";
import WBSModel from "api/models/WBSModel";
import {
  IExportImage,
  IExportMeasurementExtraData,
  IExportMeasurementRowData,
  IExportMeasurementValue,
} from "components/interfaces";
import { lang } from "i18nLang";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatNumber, formatNumberToMonetary } from "utils";
import { formatPeriodDate } from "utils/DateUtils";
import { valueToPercentual } from "utils/DecimalPrecision";

const borderWidth = 0.3;

class ExportMeasurementPDFService {
  private contractTotal = 0;

  /** Cria um documento PDF com todas as medições do contrato
   * cada uma em uma folha.
   */
  public createAllMeasurements(
    contract: ContractModel,
    measurement: MeasurementModel,
    data: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData,
    allMeasurements: {
      data: MeasurementModel;
      wbs: WBSModel<MeasurementRowModel>;
    }[]
  ): jsPDF {
    const doc = this.createPageCurrentMeasurement(
      contract,
      measurement,
      data,
      extra
    );

    if (allMeasurements?.length)
      for (const measurementData of allMeasurements) {
        if (measurementData.data.uid !== measurement.uid) {
          const wbs = measurementData.wbs.getAllRows();

          this.createPageMeasurement(
            doc,
            contract,
            measurementData.data,
            data,
            wbs,
            extra
          );
        }
      }

    return doc;
  }

  private createPageMeasurement(
    doc: jsPDF,
    contract: ContractModel,
    measurement: MeasurementModel,
    rows: IExportMeasurementRowData[],
    measurementRows: MeasurementRowModel[],
    extra: IExportMeasurementExtraData
  ) {
    const columnsCount = 10;
    const headRow = this.createHeaderRow(contract, measurement, false);
    const bodyRows: IPDFCellInput[][] = [];

    this.setBandedHeader(bodyRows, measurement, columnsCount, "", false);
    this.setTitle(bodyRows, false);
    this.setValues(bodyRows, rows, false, measurementRows);
    this.setFooter(
      bodyRows,
      contract,
      measurement,
      extra,
      columnsCount,
      false,
      rows,
      measurementRows
    );

    doc.addPage("a4", "landscape");
    this.createAutoTable(doc, headRow, bodyRows, extra.companyImage);
  }

  public createPageCurrentMeasurement(
    contract: ContractModel,
    measurement: MeasurementModel,
    rows: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData
  ): jsPDF {
    const columnsCount = 16;
    const accumulatedColumnLabel = extra.accumulatedColumnData?.label || "";
    const doc = this.createPDFDocument();

    const headRow = this.createHeaderRow(contract, measurement, true);
    const bodyRows: IPDFCellInput[][] = [];

    this.setBandedHeader(
      bodyRows,
      measurement,
      columnsCount,
      accumulatedColumnLabel,
      true
    );
    this.setTitle(bodyRows, true);
    this.setValues(bodyRows, rows, true);
    this.setFooter(
      bodyRows,
      contract,
      measurement,
      extra,
      columnsCount,
      true,
      rows
    );

    this.createAutoTable(doc, headRow, bodyRows, extra.companyImage);
    return doc;
  }

  private createPDFDocument(): jsPDF {
    const doc = new jsPDF({
      orientation: "landscape",
    });
    doc.setFontSize(8);
    doc.setTextColor(100);
    return doc;
  }

  private createAutoTable(
    doc: jsPDF,
    headRow: IPDFCellInput[][],
    bodyRows: IPDFCellInput[][],
    companyImage?: IExportImage
  ) {
    autoTable(doc, {
      headStyles: {
        fontSize: 6,
        lineWidth: borderWidth,
        textColor: "#000000",
        lineColor: "#000000",
        fillColor: "#ffffff",
      },
      bodyStyles: {
        minCellHeight: 5,
        fontSize: 6,
        lineWidth: borderWidth,
        textColor: "#000000",
        lineColor: "#000000",
      },
      head: headRow,
      body: bodyRows,
      didDrawCell: (data) => {
        if (
          data.section === "head" &&
          data.column.index === 0 &&
          companyImage
        ) {
          const base64Img = String(companyImage.base64);
          doc.addImage(
            base64Img,
            "JPEG",
            16,
            16,
            companyImage.width / 4,
            companyImage.height / 4
          );
        }
      },
      showHead: "firstPage",
      theme: "grid",
    });
  }

  /** Cria o cabeçalho com as informações gerais do contrato e medição
   * no relatório. */
  private createHeaderRow(
    contract: ContractModel,
    measurement: MeasurementModel,
    current: boolean
  ): IPDFCellInput[][] {
    return [
      [
        {
          colSpan: current ? 8 : 4,
          rowSpan: 3,
          content: "",
          styles: {
            minCellHeight: 25,
            lineWidth: {
              top: borderWidth,
              right: 0,
              bottom: borderWidth,
              left: borderWidth,
            },
          },
        },
        {
          content: lang("measurementExport.header.project"),
          styles: {
            fontStyle: "italic",
            lineWidth: { top: borderWidth, right: 0, bottom: 0, left: 0 },
          },
        },
        {
          colSpan: current ? 3 : 2,
          content: contract.development.name,
          styles: {
            lineWidth: { top: borderWidth, right: 0, bottom: 0, left: 0 },
          },
        },
        {
          content: lang("measurementExport.header.location"),
          styles: {
            fontStyle: "italic",
            lineWidth: { top: borderWidth, right: 0, bottom: 0, left: 0 },
          },
        },
        {
          colSpan: current ? 3 : 2,
          content: contract.development.local,
          styles: {
            lineWidth: {
              top: borderWidth,
              right: borderWidth,
              bottom: 0,
              left: 0,
            },
          },
        },
      ],
      [
        {
          content: lang("measurementExport.header.contract"),
          styles: { fontStyle: "italic", lineWidth: 0 },
        },
        {
          colSpan: current ? 3 : 2,
          content: contract.name,
          styles: { lineWidth: 0 },
        },
        {
          content: lang("measurementExport.header.measurement"),
          styles: { fontStyle: "italic", lineWidth: 0 },
        },
        {
          colSpan: current ? 3 : 2,
          content: measurement.description,
          styles: {
            lineWidth: { top: 0, right: borderWidth, bottom: 0, left: 0 },
          },
        },
      ],
      [
        {
          content: lang("measurementExport.header.client"),
          styles: {
            fontStyle: "italic",
            lineWidth: { top: 0, right: 0, bottom: borderWidth, left: 0 },
          },
        },
        {
          colSpan: current ? 3 : 2,
          content: contract.development.customer,
          styles: {
            lineWidth: { top: 0, right: 0, bottom: borderWidth, left: 0 },
          },
        },
        {
          content: lang("measurementExport.header.period"),
          styles: {
            fontStyle: "italic",
            lineWidth: { top: 0, right: 0, bottom: borderWidth, left: 0 },
          },
        },
        {
          colSpan: current ? 3 : 2,
          content: formatPeriodDate(
            measurement.initialDate,
            measurement.finalDate
          ),
          styles: {
            lineWidth: {
              top: 0,
              right: borderWidth,
              bottom: borderWidth,
              left: 0,
            },
          },
        },
      ],
    ];
  }

  /** Define os títulos dos grupos de colunas do relatório
   * com suas devidas formatações, e mergeando as células
   * necessárias.
   */
  private setBandedHeader(
    bodyRows: IPDFCellInput[][],
    measurement: MeasurementModel,
    colsCount: number,
    accumulatedTitle: string,
    current: boolean
  ) {
    const description = measurement.description.toUpperCase();
    const styles: Partial<IPDFCellStyles> = {
      fontSize: 8,
      fontStyle: "bold",
      halign: "center",
    };

    bodyRows.push([
      {
        colSpan: 7,
        rowSpan: current ? 2 : undefined,
        content: lang("measurementExport.bands.contract").toLocaleUpperCase(),
        styles: {
          ...styles,
          valign: "middle",
        },
      },
      {
        colSpan: colsCount - 7,
        content: description,
        styles,
      },
    ]);

    if (current) {
      bodyRows.push([
        {
          colSpan: 3,
          content: accumulatedTitle.toLocaleUpperCase(),
          styles,
        },
        {
          colSpan: 3,
          content: lang("measurementExport.bands.actual").toLocaleUpperCase(),
          styles,
        },
        {
          colSpan: 3,
          content: lang("measurementExport.bands.balance").toLocaleUpperCase(),
          styles,
        },
      ]);
    }
  }

  /** Define os títulos de cada coluna do relatório com
   * suas formatações.
   */
  private setTitle(bodyRows: IPDFCellInput[][], current: boolean) {
    const unitCostLabel = `${lang("fields.unitCostShort")} (R$)`;
    const totalLabel = `${lang("fields.total")} (R$)`;
    const quantityLabel = lang("fields.quantity");

    const quantityStyles: Partial<IPDFCellStyles> = {
      fontStyle: "italic",
      halign: "center",
      minCellWidth: 15,
    };
    const percentageStyles: Partial<IPDFCellStyles> = {
      fontStyle: "italic",
      halign: "center",
    };
    const totalStyles: Partial<IPDFCellStyles> = {
      fontStyle: "italic",
      halign: "center",
      minCellWidth: 20,
    };

    const row: IPDFCellInput[] = [
      { content: lang("fields.code"), styles: { fontStyle: "italic" } },
      { content: lang("fields.description"), styles: { fontStyle: "italic" } },
      { content: quantityLabel, styles: quantityStyles },
      { content: lang("fields.unit"), styles: percentageStyles },
      { content: unitCostLabel, styles: totalStyles },
      { content: totalLabel, styles: totalStyles },
      { content: "%", styles: percentageStyles },
      { content: quantityLabel, styles: quantityStyles },
      { content: "%", styles: percentageStyles },
      { content: totalLabel, styles: totalStyles },
    ];

    if (current) {
      row.push({ content: quantityLabel, styles: quantityStyles });
      row.push({ content: "%", styles: percentageStyles });
      row.push({ content: totalLabel, styles: totalStyles });
      row.push({ content: quantityLabel, styles: quantityStyles });
      row.push({ content: "%", styles: percentageStyles });
      row.push({ content: totalLabel, styles: totalStyles });
    }

    bodyRows.push(row);
  }

  /** Define os valores de todas as linha da medição no relatório
   * com suas formatações.
   */
  private setValues(
    bodyRows: IPDFCellInput[][],
    rowDatas: IExportMeasurementRowData[],
    current: boolean,
    measurement?: MeasurementRowModel[]
  ) {
    rowDatas.forEach((row, index) => {
      const fontStyle = row.rowType === ERowTypes.STAGE ? "bold" : "normal";
      const fillColor = index % 2 === 0 ? "D9D9D9" : undefined;
      const defaultStyles: Partial<IPDFCellStyles> = {
        fontStyle,
        fillColor,
      };
      const quantityStyles: Partial<IPDFCellStyles> = {
        ...defaultStyles,
        halign: "right",
        minCellWidth: 15,
      };
      const totalStyles: Partial<IPDFCellStyles> = {
        ...defaultStyles,
        halign: "right",
        minCellWidth: 20,
      };
      const quantity =
        row.rowType === ERowTypes.RESOURCE ? formatNumber(row.quantity, 2) : "";
      const unitCost =
        row.rowType === ERowTypes.RESOURCE
          ? formatNumberToMonetary(row.unitCost)
          : "";

      const rowTable: IPDFCellInput[] = [
        { content: row.structured, styles: defaultStyles },
        { content: row.description, styles: defaultStyles },
        {
          content: quantity,
          styles: quantityStyles,
        },
        {
          content: row.unit,
          styles: { ...defaultStyles, halign: "center" },
        },
        {
          content: unitCost,
          styles: totalStyles,
        },
        {
          content: formatNumberToMonetary(row.total),
          styles: totalStyles,
        },
        {
          content: `${formatNumber(row.percentual, 2)}%`,
          styles: { ...defaultStyles, halign: "right" },
        },
      ];

      if (current) {
        rowTable.push(
          ...this.setMeasurementCells(row.rowType, row.measured, defaultStyles)
        );
        rowTable.push(
          ...this.setMeasurementCells(
            row.rowType,
            row.currentMeasurement,
            defaultStyles
          )
        );
        rowTable.push(
          ...this.setMeasurementCells(
            row.rowType,
            row.currentToBilling,
            defaultStyles
          )
        );
      } else {
        let data: IExportMeasurementValue = {
          total: "-",
          quantity: "-",
          percentual: "-",
        };
        const currentMeasurement = measurement?.find(
          (measurementRow) => measurementRow.uid === row.uid
        );

        if (currentMeasurement) {
          data = {
            ...currentMeasurement,
            percentual: valueToPercentual(currentMeasurement.total, row.total),
          };
        }

        rowTable.push(
          ...this.setMeasurementCells(row.rowType, data, defaultStyles)
        );
      }

      bodyRows.push(rowTable);
    });
  }

  private setMeasurementCells(
    rowType: number,
    data: {
      quantity: number | string;
      percentual: number | string;
      total: number | string;
    },
    styles: Partial<IPDFCellStyles>
  ): IPDFCellInput[] {
    const percentual =
      data.percentual !== "-" ? `${formatNumber(data.percentual, 2)}%` : "-";

    let quantity = data.quantity !== "-" ? formatNumber(data.quantity, 2) : "-";
    if (rowType === ERowTypes.STAGE) {
      quantity = "";
    }

    let total = "-";
    let color = "000000";
    if (data.total !== "-") {
      total = formatNumberToMonetary(data.total);
      color = Number(data.total) < 0 ? "ff0000" : "000000";
    }

    const hAlign = total === "-" ? "center" : "right";
    return [
      {
        content: quantity,
        styles: {
          ...styles,
          halign: hAlign,
          minCellWidth: 15,
          textColor: color,
        },
      },
      {
        content: percentual,
        styles: { ...styles, halign: hAlign, textColor: color },
      },
      {
        content: total,
        styles: {
          ...styles,
          halign: hAlign,
          minCellWidth: 20,
          textColor: color,
        },
      },
    ];
  }

  /** Define o rodapé do relatório com suas devidas
   * formatações e mergeando as células necessárias.
   */
  private setFooter(
    bodyRows: IPDFCellInput[][],
    contract: ContractModel,
    measurement: MeasurementModel,
    extra: IExportMeasurementExtraData,
    colsCount: number,
    current: boolean,
    data: IExportMeasurementRowData[],
    measurementRows?: MeasurementRowModel[]
  ) {
    const footerRows: IPDFCellInput[][] = [];
    this.createCellsWithDataInFooter(
      footerRows,
      measurement,
      current,
      data,
      measurementRows
    );

    if (current) {
      footerRows.push(this.createBlankColumns([], 16)); // Linha em branco
      this.createSignSpaceInFooter(
        footerRows,
        extra.companyName || "",
        contract.development.customer
      );
    }

    bodyRows.push(...footerRows);
  }

  /** Cria as linhas com os totais da medição e do contrato no rodapé
   * do relatório.
   */
  private createCellsWithDataInFooter(
    footerRows: IPDFCellInput[][],
    measurement: MeasurementModel,
    current: boolean,
    data: IExportMeasurementRowData[],
    measurementRows?: MeasurementRowModel[]
  ) {
    const { measurementTotal, measuredTotal, billingTotal } =
      this.calculateTotals(current, data, measurementRows);

    footerRows.push(
      this.setFooterCells(
        lang(`measurementExport.footer.totals`),
        measurementTotal,
        current,
        this.contractTotal,
        measuredTotal,
        billingTotal
      )
    );
    footerRows.push(
      this.setFooterCells(
        lang(`measurementExport.footer.discount`),
        measurement.getAllDiscountsTotal(),
        current
      )
    );
    footerRows.push(
      this.setFooterCells(
        lang(`measurementExport.footer.taxes`),
        measurement.getTaxesTotal(),
        current
      )
    );
    footerRows.push(
      this.setFooterCells(
        lang(`measurementExport.footer.retention`),
        measurement.getTechnicalRetentionTotal(),
        current
      )
    );
    footerRows.push(
      this.setFooterCells(
        lang(`measurementExport.footer.advance`),
        measurement.getAdvancesTotal(),
        current
      )
    );
    footerRows.push(
      this.setFooterCells(
        lang(`measurementExport.footer.invoice`),
        measurement.getGrossTotal(),
        current
      )
    );
  }

  private calculateTotals(
    current: boolean,
    data: IExportMeasurementRowData[],
    measurementRows?: MeasurementRowModel[]
  ) {
    let measurementTotal = 0;
    let measuredTotal = 0;
    let billingTotal = 0;

    if (current) {
      // É calculado na primeira página e reaproveitado nas próximas
      this.contractTotal = 0;

      data.forEach((row) => {
        if (row.structured.includes(".")) return;

        this.contractTotal += row.total;
        const measureValue = row.currentMeasurement.total;
        if (measureValue !== "-") {
          measurementTotal += Number(measureValue);
        }

        billingTotal += Number(row.currentToBilling.total);
        if (row.measured.total !== "-") {
          measuredTotal += Number(row.measured.total);
        }
      });
    } else {
      measurementRows?.forEach((row) => {
        if (row.structured.includes(".")) return;
        measurementTotal += row.total;
      });
    }

    return {
      measurementTotal,
      measuredTotal,
      billingTotal,
    };
  }

  private setFooterCells(
    title: string,
    total: number,
    current: boolean,
    contractTotal?: number,
    accumulatedTotal?: number,
    billingTotal?: number
  ): IPDFCellInput[] {
    const rows: IPDFCellInput[] = [
      { content: "" },
      {
        content: title,
        styles: { fontStyle: "italic", halign: "right" },
      },
    ];

    this.createBlankColumns(rows, 3);
    rows.push({
      content:
        contractTotal !== undefined
          ? formatNumberToMonetary(contractTotal)
          : "",
      styles: { fontStyle: "bold", halign: "right" },
    });

    this.createBlankColumns(rows, 3);
    const firstTotal = current ? accumulatedTotal : total;
    rows.push({
      content:
        firstTotal !== undefined ? formatNumberToMonetary(firstTotal) : "",
      styles: { fontStyle: "bold", halign: "right" },
    });

    if (!current) return rows;

    this.createBlankColumns(rows, 2);
    rows.push({
      content: total !== undefined ? formatNumberToMonetary(total) : "",
      styles: { fontStyle: "bold", halign: "right" },
    });

    this.createBlankColumns(rows, 2);
    rows.push({
      content:
        billingTotal !== undefined ? formatNumberToMonetary(billingTotal) : "",
      styles: { fontStyle: "bold", halign: "right" },
    });

    return rows;
  }

  /** Adiciona as assinaturas de empresa e cliente,
   * mergea e formata as outras células para deixar o
   * espaço em branco.
   */
  private createSignSpaceInFooter(
    footerRows: IPDFCellInput[][],
    companyName: string,
    customerName: string
  ) {
    const rowsLine = this.createBlankColumns([], 9);
    const rowsName = this.createBlankColumns([], 9);

    for (let i = 0; i < 5; i++) {
      let contentA = "";
      let contentB = "";

      if (i === 1) {
        contentA = "____________________";
        contentB = companyName;
      }

      if (i === 3) {
        contentA = "____________________";
        contentB = customerName;
      }

      rowsLine.push({
        content: contentA,
        styles: {
          lineWidth: {
            top: borderWidth,
            right: i === 5 ? borderWidth : 0,
            bottom: borderWidth,
            left: i === 0 ? borderWidth : 0,
          },
          halign: "center",
        },
      });
      rowsName.push({
        content: contentB,
        styles: {
          lineWidth: borderWidth,
          halign: "center",
        },
      });
    }

    this.createBlankColumns(rowsLine, 2);
    this.createBlankColumns(rowsName, 2);

    footerRows.push(rowsLine);
    footerRows.push(rowsName);
  }

  private createBlankColumns(rows: IPDFCellInput[], qtdeColumns: number) {
    for (let i = 0; i < qtdeColumns; i++) {
      rows.push({ content: "" });
    }
    return rows;
  }
}

export default new ExportMeasurementPDFService();
